import head from 'lodash/head'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TicketCard } from '@/components/TicketCard'
import { Service, ServiceAvailability } from '@/models/service'

import { serviceQuantitySelector } from '@/store/cartSlice/selectors/serviceQuantitySelector'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import { SERVICE_LABEL_LIMIT } from '@/utils/constants'
import { SINGLE_DAY_PRICE_MODES } from '@/utils/timeSchedules'
import { useAppSelector } from '../../hooks/store'

interface Props {
  availability: ServiceAvailability
  ticket: Service
}

export const Ticket = (props: Props) => {
  const { t } = useTranslation()

  const quantity = useAppSelector((state) =>
    serviceQuantitySelector(state, props.ticket)
  )
  const dayDifference = useAppSelector(dayDifferenceSelector)

  const hideTicket = useMemo<boolean>(() => {
    if (props.ticket.timeSchedules.length === 0) {
      return true
    }

    if (
      props.ticket.minimumQuantity > 0 ||
      props.ticket.scheduleType !== 'daily'
    ) {
      return false
    }

    const daysLength = Object.keys(props.ticket.dailyTimeSchedules).length
    if (daysLength === 0) {
      return true
    }

    if (daysLength !== dayDifference) {
      const firstSchedule = head(props.ticket.timeSchedules)
      if (!firstSchedule) {
        // this should never happen
        return true
      }

      return !SINGLE_DAY_PRICE_MODES.includes(firstSchedule.priceMode)
    }

    return false
  }, [props.ticket])

  if (hideTicket) {
    return null
  }

  const global = props.availability?.global ?? Infinity
  const group = props.availability?.group ?? Infinity
  const ticket = props.availability?.ticket ?? Infinity

  const availableQuantity = props.ticket.hasLimit
    ? Math.min(global, group, ticket - quantity)
    : Infinity

  const isAddDisabled = availableQuantity < 1
  const isRemoveDisabled =
    quantity === 0 || quantity <= props.ticket.minimumQuantity

  return (
    <TicketCard
      isAddDisable={isAddDisabled}
      isRemoveDisabled={isRemoveDisabled}
      maxLabel={
        props.ticket.scheduleType === 'daily' &&
        props.availability.ticket < SERVICE_LABEL_LIMIT
          ? t('listItem.max', { value: props.availability.ticket })
          : ''
      }
      minLabel={t('listItem.min', { value: props.ticket.minimumQuantity })}
      quantity={quantity}
      ticket={props.ticket}
    />
  )
}
